import React, { useEffect, useState } from "react";
import StaffNavBar from "../components/StaffNavBar";
import {
  AdminPageBody,
  AdminPanel,
  AdminLoadingState,
  adminButton,
  adminPrimaryButton,
  adminDangerButton,
} from "../components/AdminPageShell";
import { useStaffCategories } from "../store/staff";
import {useConfirmDialog} from "../components/ConfirmDialogProvider";

const emptyForm = { name: "", description: "", image: "" };
const inputClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-[#16213A] focus:ring-2 focus:ring-[#16213A]/15";

export default function StaffCategories() {
  const confirm = useConfirmDialog();
  const {
    staffCategories,
    staffCategoriesLoading,
    staffCategoriesError,
    staffCategoryDetailLoading,
    staffCategoryDetailError,
    staffCategoryMutationLoading,
    staffCategoryMutationError,
    fetchStaffCategories,
    fetchStaffCategory,
    createStaffCategory,
    updateStaffCategory,
    deleteStaffCategory,
  } = useStaffCategories();
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetchStaffCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const edit = async (id) => {
    const result = await fetchStaffCategory(id);
    if (!result.success) return;
    const category = result.data;
    setEditingId(id);
    setForm({
      name: category.name || "",
      description: category.description || "",
      image: category.image || "",
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    // CreateCategoryRequest/UpdateCategoryRequest are strict: only send
    // populated fields (an empty image string fails the URL validator).
    const payload = { name: form.name.trim() };
    if (form.description.trim()) payload.description = form.description.trim();
    if (form.image.trim()) payload.image = form.image.trim();

    const result = editingId
      ? await updateStaffCategory(editingId, payload)
      : await createStaffCategory(payload);
    if (!result.success) return;
    setMessage(editingId ? "Category updated." : "Category created.");
    setEditingId(null);
    setForm(emptyForm);
    await fetchStaffCategories();
  };

  const remove = async (id) => {
    return confirm({
      title: "Delete this category?",
      description: "The category must not have any courses. This action cannot be undone.",
      confirmLabel: "Delete category",
      onConfirm: async () => {
        const result = await deleteStaffCategory(id);
        if (!result.success) throw new Error(result.error || "Could not delete category.");
        setMessage("Category deleted.");
        await fetchStaffCategories();
      },
    });
  };

  return (
    <div>
      <StaffNavBar title="Categories" context="Manage course categories." />
      <AdminPageBody>
        <AdminPanel className="p-5">
          <h2 className="mb-4 mt-0 text-lg font-semibold text-slate-900">
            {editingId ? "Update category" : "Create category"}
          </h2>
          {staffCategoryMutationError && <p className="text-sm text-red-700">{staffCategoryMutationError}</p>}
          {message && <p className="text-sm text-green-700">{message}</p>}
          <form className="grid gap-3 sm:grid-cols-3" onSubmit={submit}>
            <label className="text-xs font-semibold text-slate-600">
              Name
              <input className={`${inputClass} mt-1`} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Description
              <input className={`${inputClass} mt-1`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Image URL
              <input className={`${inputClass} mt-1`} type="url" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
            </label>
            <div className="flex gap-2 sm:col-span-3">
              <button className={adminPrimaryButton} disabled={staffCategoryMutationLoading}>
                {editingId ? "Save changes" : "Create category"}
              </button>
              {editingId && (
                <button type="button" className={adminButton} onClick={() => { setEditingId(null); setForm(emptyForm); }}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </AdminPanel>
        <AdminPanel className="overflow-hidden">
          {staffCategoriesError && <p className="px-4 text-sm text-red-700">{staffCategoriesError}</p>}
          {staffCategoryDetailError && <p className="px-4 text-sm text-red-700">{staffCategoryDetailError}</p>}
          {staffCategoryDetailLoading && <p className="px-4 text-sm text-slate-500">Loading category details…</p>}
          {staffCategoriesLoading ? <AdminLoadingState /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3">Description</th><th className="px-5 py-3 text-center">Actions</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {staffCategories.map((category) => (
                    <tr key={category.id}>
                      <td className="px-5 py-3 font-medium text-slate-900">{category.name}</td>
                      <td className="px-5 py-3 text-slate-600">{category.description || "—"}</td>
                      <td className="px-5 py-3">
                        <div className="flex justify-center gap-2">
                          <button className={adminButton} onClick={() => edit(category.id)}>View / edit</button>
                          <button className={adminDangerButton} disabled={staffCategoryMutationLoading} onClick={() => remove(category.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!staffCategories.length && !staffCategoriesLoading && (
                    <tr><td colSpan={3} className="px-5 py-10 text-center text-slate-500">No categories found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </AdminPanel>
      </AdminPageBody>
    </div>
  );
}
